import { useEffect, useRef, useState, type RefObject } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FaMicrophone, FaRegFileAlt, FaUser, FaRegCheckSquare, FaRegEnvelope } from 'react-icons/fa'
import { FiSearch, FiLoader, FiMenu, FiX } from 'react-icons/fi'
import { BsChatDots } from 'react-icons/bs'
import { MdQuestionMark } from 'react-icons/md'
import { searchCrm } from '../api/crm'
import { getSessionUserId } from '../utils/sessionUser'
import { CiCalendar } from "react-icons/ci";

import AudioCaptureWrapper from './AudioCaptureWrapper'
import ListenModeButton from './ListenModeButton'
import '../style/layout/topbar.css'

type SearchResult = {
  id: string
  type: 'CHAT' | 'CALL' | 'EML' | 'CLT' | 'AI' | 'CAL'
  title?: string
  snippet?: string
  dateCreated?: string
  tags?: string[] | string | null
}

function getResultIcon(type: string) {
  const t = type.toUpperCase()
  switch (t) {
    case 'CHAT': return <BsChatDots />
    case 'CALL': return <FaRegFileAlt />
    case 'EML': return <FaRegEnvelope />
    case 'CLT': return <FaUser />
    case 'AI': return <FaRegCheckSquare />
    case 'CAL': return <CiCalendar />
    default: return <MdQuestionMark />
  }
}

type SearchResultsDropdownProps = {
  searchOpen: boolean
  query: string
  results: SearchResult[]
  isSearching: boolean
  onResultClick: (result: SearchResult) => void
}

function SearchResultsDropdown({ searchOpen, query, results, isSearching, onResultClick }: SearchResultsDropdownProps) {
  if (!searchOpen || (!query && results.length === 0)) return null

  return (
    <div className="search-results-dropdown">
      {isSearching && (
        <div className="search-loading">
          <FiLoader className="spin" />
          <span>Searching...</span>
        </div>
      )}
      {!isSearching && query && results.length === 0 && (
        <div className="search-empty">No results found for "{query}"</div>
      )}
      <div className="search-results-list">
        {results.map((result) => {
          const tag = Array.isArray(result.tags) ? result.tags[0] : result.tags
          const rType = result.type.toUpperCase()
          const isEvent = rType === 'CAL'

          return (
            <button
              type="button"
              key={`${result.type}-${result.id}`}
              className="search-result-item"
              onClick={() => onResultClick(result)}
            >
              <div className={`result-icon ${result.type.toLowerCase()} ${isEvent ? 'cal' : ''}`}>
                {getResultIcon(result.type)}
              </div>
              <div className="result-info">
                <div className="result-title">
                  {result.title}
                  {isEvent && tag && (
                    <span className={`cp-tag-badge ${tag.toLowerCase()}`}>{tag}</span>
                  )}
                </div>
                <div className="result-snippet">
                  {result.snippet && result.snippet !== 'null' ? result.snippet : ''}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

type TopbarProps = {
  assistantButtonRef: RefObject<HTMLButtonElement | null>
  hidden?: boolean
  userInitials: string
  onSettings: () => void
  onLogout: () => void
  onToggleMenu?: () => void
  mobileNavOpen?: boolean
  searchOpen: boolean
  setSearchOpen: (open: boolean) => void
}

function Topbar({
  assistantButtonRef,
  hidden,
  userInitials,
  onSettings,
  onLogout,
  onToggleMenu,
  mobileNavOpen,
  searchOpen,
  setSearchOpen,
}: TopbarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const avatarButtonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const searchTriggerRef = useRef<HTMLButtonElement | null>(null)
  const [searchOrigin, setSearchOrigin] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  // Search State
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (avatarButtonRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (!searchOpen) return
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (searchRef.current?.contains(target)) return
      setSearchOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [searchOpen, setSearchOpen])

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus()
    } else {
      setQuery('')
      setResults([])
    }
  }, [searchOpen])

  useEffect(() => {
    const syncSize = () => setIsMobile(window.innerWidth <= 1100)
    syncSize()
    window.addEventListener('resize', syncSize)
    return () => window.removeEventListener('resize', syncSize)
  }, [])

  useEffect(() => {
    if (!isMobile) setSearchOpen(false)
  }, [isMobile, setSearchOpen])

  // Fetch Search Results
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)

    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const userId = getSessionUserId()
        const data = await searchCrm(query.trim(), userId || '')
        setResults(data)
      } catch (err) {
        console.error('Search failed', err)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [query])

  const openSearch = () => {
    const triggerRect = searchTriggerRef.current?.getBoundingClientRect()
    if (triggerRect) {
      const triggerCenter = triggerRect.left + triggerRect.width / 2
      const deltaX = triggerCenter - window.innerWidth / 2
      setSearchOrigin(deltaX)
    } else {
      setSearchOrigin(0)
    }
    setSearchOpen(true)
  }

  const handleResultClick = (result: SearchResult) => {
    setSearchOpen(false)
    setQuery('')
    const rType = result.type.toUpperCase()
    switch (rType) {
      case 'CHAT':
        navigate(`/assistant?id=${result.id}`)
        break
      case 'CALL':
        navigate(`/conversation/${result.id}`)
        break
      case 'EML': {
        const cid = result.id.startsWith('email_') ? result.id.replace('email_', 'conv_') : result.id
        navigate(`/conversation/${cid}`)
        break
      }
      case 'CLT':
        navigate(`/contact/${result.id}`)
        break
      case 'AI':
        navigate('/action-items')
        break
      case 'CAL':
        navigate(`/calendar#${result.id}`)
        break
    }
  }

  return (
    <header className={`topbar ${hidden ? 'hidden' : ''} ${searchOpen ? 'searching' : ''}`}>
      <div className="topbar-shield" />
      <div className="topbar-left">
        {onToggleMenu && isMobile && (
          <button
            type="button"
            className={`icon-button menu-button ${mobileNavOpen ? 'open' : ''}`}
            aria-label="Toggle navigation"
            onClick={onToggleMenu}
          >
            {mobileNavOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
        )}
        <div className="search-group" ref={searchRef}>
          {searchOpen && (
            <div
              className="search-overlay open"
              onClick={() => setSearchOpen(false)}
            />
          )}
          {isMobile ? (
            <>
              <div className={`search-shell ${searchOpen ? 'open' : ''}`}>
                <button
                  className={`icon-button search-trigger ${searchOpen ? 'active' : ''}`}
                  aria-label="Search"
                  onClick={openSearch}
                  ref={searchTriggerRef}
                >
                  <FiSearch className="search-icon" aria-hidden size={18} />
                </button>
                <div
                  className="search expanded"
                  style={{ ['--search-origin' as string]: `${searchOrigin}px` }}
                >
                  <FiSearch className="search-icon" aria-hidden size={18} />
                  <input
                    placeholder="Search anything..."
                    ref={searchInputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setSearchOpen(true)}
                    tabIndex={searchOpen ? 0 : -1}
                    aria-hidden={!searchOpen}
                  />
                  <SearchResultsDropdown searchOpen={searchOpen} query={query} results={results} isSearching={isSearching} onResultClick={handleResultClick} />
                </div>
              </div>
              <AudioCaptureWrapper>
                {({ isRecording, isLoading, isConnected, startRecording, stopRecording }) => {
                  const toggleRecord = () => {
                    if (isRecording) stopRecording()
                    else startRecording()
                  }

                  return (
                    <button
                      type="button"
                      className={`record-button ${isRecording ? 'active' : ''} ${
                        isConnected === 'CONNECTED' ? 'connected' : ''
                      }`}
                      onClick={toggleRecord}
                      disabled={isLoading}
                      aria-label={isRecording ? 'Stop recording' : 'Start recording'}
                    >
                      <FaMicrophone aria-hidden size={18} />
                    </button>
                  )
                }}
              </AudioCaptureWrapper>
            </>
          ) : (
            <>
              <div className={`search ${searchOpen ? 'active' : ''}`}>
                <FiSearch className="search-icon" aria-hidden size={18} />
                <input
                  placeholder="Search anything..."
                  ref={searchInputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                />
                <SearchResultsDropdown searchOpen={searchOpen} query={query} results={results} isSearching={isSearching} onResultClick={handleResultClick} />
              </div>
              <AudioCaptureWrapper instanceId="mic">
                {({ isRecording, isLoading, isConnected, startRecording, stopRecording }) => {
                  const toggleRecord = () => {
                    if (isRecording) stopRecording()
                    else startRecording()
                  }

                  return (
                    <button
                      type="button"
                      className={`record-button ${isRecording ? 'active' : ''} ${
                        isConnected === 'CONNECTED' ? 'connected' : ''
                      }`}
                      onClick={toggleRecord}
                      disabled={isLoading}
                      aria-label={isRecording ? 'Stop recording' : 'Start recording'}
                    >
                      <FaMicrophone aria-hidden size={18} />
                    </button>
                  )
                }}
              </AudioCaptureWrapper>
              <ListenModeButton />
            </>
          )}
        </div>
      </div>
      <div className="topbar-actions">
        <button
          ref={assistantButtonRef}
          className={`ai-button ${isMobile ? 'icon-only' : ''} ${location.pathname === '/assistant' ? 'active' : ''}`}
          type="button"
          onClick={() => {
            if (location.pathname === '/assistant') {
              navigate(-1)
            } else {
              navigate('/assistant')
            }
          }}
          aria-label="AI Assistant"
        >
          {isMobile ? (
            <span className="ai-icon" aria-hidden>
              🤖
            </span>
          ) : (
            <span className="ai-label">Chat with Bev</span>
          )}
        </button>
        <div className="avatar-wrap">
          <button
            ref={avatarButtonRef}
            className={`avatar logout-button ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Account menu"
            title="Account menu"
          >
            {userInitials}
          </button>
          {menuOpen && (
            <div className="avatar-menu" ref={menuRef}>
              <button type="button" onClick={() => { setMenuOpen(false); onSettings() }}>
                Settings
              </button>
              <button type="button" onClick={() => { setMenuOpen(false); onLogout() }}>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Topbar
